import { Box, Heading, Image, Input, Stack, Text } from "@chakra-ui/react";
import { useState } from "react";
import RakutenKeyInstruction1 from "../assets/rakuten-key-instruction-1.png";
import RakutenKeyInstruction2 from "../assets/rakuten-key-instruction-2.png";
import RakutenKeyInstruction3 from "../assets/rakuten-key-instruction-3.png";
import RakutenKeyInstruction4 from "../assets/rakuten-key-instruction-4.png";
import RakutenKeyInstruction5 from "../assets/rakuten-key-instruction-5.png";
import RakutenKeyInstruction6 from "../assets/rakuten-key-instruction-6.png";
import { useCreateIntegrationMutation } from "../graphql/generated/apollo";
import { useShopContext } from "../shop";
import { Button } from "./ui/button";
import { toaster } from "./ui/toaster";

type RakutenIntegrationSettingsFormProps = {
  setShowRakutenSettingForm: React.Dispatch<React.SetStateAction<boolean>>;
};

export function RakutenIntegrationSettingsForm(props: RakutenIntegrationSettingsFormProps) {
  const { currentShopId, isLoading: isShopLoading } = useShopContext();
  const [serviceSecretKey, setServiceSecretKey] = useState("");
  const [licenseKey, setLicenseKey] = useState("");
  const [createIntegration] = useCreateIntegrationMutation();

  const registerKeys = () => {
    createIntegration({
      variables: {
        input: {
          shopId: currentShopId ?? "",
          platform: "RAKUTEN",
          serviceSecretKey: serviceSecretKey,
          licenseKey: licenseKey,
        },
      },
    })
      .then((resp) => {
        if (resp.data?.createIntegration) {
          toaster.success({
            title: "APIキーの登録に成功しました",
          });
          props.setShowRakutenSettingForm(false);
        } else {
          alert("APIキーの登録に失敗しました");
        }
      })
      .catch((e) => {
        console.error(e);
        alert(`APIキーの登録に失敗しました: ${e}`);
      });
  };

  return (
    <Stack gap={6}>
      <Box>
        <Heading size="sm" mb={4}>
          1. APIの利用設定
        </Heading>

        <Box mb={6}>
          <Text fontWeight="bold">Step 1: 店舗様向け情報・サービスをクリックする</Text>
          <Text mb={2} fontSize="sm">
            店舗様向け情報・サービスをクリックして、WEB APIサービスの画面へ移動します。
          </Text>
          <Image src={RakutenKeyInstruction1} alt="店舗様向け情報・サービスの画面" borderRadius="md" />
        </Box>

        <Box mb={6}>
          <Text fontWeight="bold">Step 2: WEB APIサービスをクリックする</Text>
          <Text mb={2} fontSize="sm">
            表示されたメニューからWEB APIサービスを選択します。
          </Text>
          <Image src={RakutenKeyInstruction2} alt="WEB APIサービスの選択画面" borderRadius="md" />
        </Box>

        <Box mb={6}>
          <Text fontWeight="bold">Step 3: 利用機能編集をクリックする</Text>
          <Text mb={2} fontSize="sm">
            利用機能一覧から「利用機能編集」をクリックしてください。
          </Text>
          <Image src={RakutenKeyInstruction3} alt="利用機能編集の画面" borderRadius="md" />
        </Box>

        <Box mb={6}>
          <Text fontWeight="bold">Step 4: 全て選択をクリックする</Text>
          <Text mb={2} fontSize="sm">
            各APIごとに<strong>全て選択</strong>をクリックします。
            <br />※ 〇〇APIは21個ありますので、各APIに対して全て選択を行ってください。
          </Text>
          <Image src={RakutenKeyInstruction4} alt="全て選択の操作画面" borderRadius="md" />
        </Box>
      </Box>

      <Box>
        <Heading size="sm" mb={4}>
          2. サービスシークレットキーとライセンスキーの登録
        </Heading>
        <Text mb={4} fontSize="sm" color="gray.600">
          RMS管理画面からコピーできる2つのキーを、以下の入力フォームに貼り付けて登録していただきます。下記のStepに沿ってRMS管理画面上で各キーをコピーし、フォームに貼り付けて登録してください。
        </Text>
        <Box mb={6}>
          <Input
            placeholder="サービスシークレットキー"
            value={serviceSecretKey}
            onChange={(e) => setServiceSecretKey(e.target.value)}
            mb={4}
          />
          <Input
            placeholder="ライセンスキー"
            value={licenseKey}
            onChange={(e) => setLicenseKey(e.target.value)}
            mb={4}
          />
          <Button
            onClick={registerKeys}
            colorPalette="teal"
            loading={isShopLoading}
            disabled={!licenseKey || !serviceSecretKey}
          >
            登録する
          </Button>
        </Box>

        <Box mb={6}>
          <Text fontWeight="bold">Step 1: サービスシークレットキーをコピーする</Text>
          <Text mb={2} fontSize="sm">
            画面に表示されたサービスシークレットキーをコピーし、<strong>上記の入力フォームに貼り付けて</strong>
            ください。
          </Text>
          <Image src={RakutenKeyInstruction5} alt="サービスシークレットキーのコピー画面" borderRadius="md" />
        </Box>

        <Box mb={6}>
          <Text fontWeight="bold">Step 2: ライセンスキーを確認・コピーする</Text>
          <Text mb={2} fontSize="sm">
            ライセンスキーの確認・変更画面からキーをコピーし、<strong>上記の入力フォームに貼り付けて</strong>ください。
          </Text>
          <Image src={RakutenKeyInstruction6} alt="ライセンスキーの確認画面" borderRadius="md" />
        </Box>

        <Box mb={6}>
          <Text fontWeight="bold">Step 3: キーを登録する</Text>
          <Text mb={2} fontSize="sm">
            サービスシークレットキーとライセンスキーを上記の入力フォームに貼り付けたら、入力フォームの下にある「登録する」ボタンをクリックしてください
          </Text>
        </Box>
      </Box>
    </Stack>
  );
}
